import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "@/utils/motion";
import "./DotField.css";

/**
 * The Devory environment: a dotted field sitting under a near-opaque black
 * cover, everywhere, including the homepage hero — one consistent
 * treatment for the whole site rather than a special hero band. The
 * pointer doesn't add a glow on top of the scene, it thins the cover
 * locally so the field underneath becomes faintly visible, like a reveal
 * rather than a light. `--pointer-x`/`--pointer-y` are declared as
 * animatable in DotField.css (`@property`), so the cover's own CSS
 * transition eases toward each new position instead of snapping straight
 * to it — that lag is what reads as a short trail following the cursor,
 * with no extra elements or per-frame JS easing math needed. Mounted once
 * for the whole site (see RootLayout.tsx), not duplicated per page.
 *
 * Performance: pointer position is read in a native `pointermove` listener
 * and written straight to a CSS custom property on the cover element via
 * `requestAnimationFrame` throttling — no React state, no re-render, no
 * per-frame layout work. `pointermove`/`pointerdown` are passive so they
 * never block scrolling. `pointermove` alone (no `pointerType` branching) is
 * enough to cover touch correctly: a touch pointer only emits `pointermove`
 * while a finger is actually in contact, so the reveal is inherently
 * touch-active-only without special-casing it.
 */
export function DevoryBackground() {
  const coverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const el = coverRef.current;
    if (!el) return;

    let frame = 0;
    let pendingX = 0;
    let pendingY = 0;

    const apply = () => {
      frame = 0;
      el.style.setProperty("--pointer-x", `${pendingX}px`);
      el.style.setProperty("--pointer-y", `${pendingY}px`);
    };

    const handleMove = (event: PointerEvent) => {
      pendingX = event.clientX;
      pendingY = event.clientY;
      if (!frame) frame = requestAnimationFrame(apply);
    };

    const reset = () => {
      el.style.setProperty("--pointer-x", "-9999px");
      el.style.setProperty("--pointer-y", "-9999px");
    };

    window.addEventListener("pointermove", handleMove, { passive: true });
    window.addEventListener("pointerdown", handleMove, { passive: true });
    window.addEventListener("pointerup", reset, { passive: true });
    window.addEventListener("pointercancel", reset, { passive: true });
    document.documentElement.addEventListener("pointerleave", reset, { passive: true });

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerdown", handleMove);
      window.removeEventListener("pointerup", reset);
      window.removeEventListener("pointercancel", reset);
      document.documentElement.removeEventListener("pointerleave", reset);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="devory-background" aria-hidden="true">
      <div className="devory-background-dots" />
      <div ref={coverRef} className="devory-background-cover" />
      <div className="devory-background-vignette" />
    </div>
  );
}
